package br.com.duoma.duomaproducao.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.VerticalScrollbar
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollbarAdapter
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.Key.Companion.Period
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.duoma.duomaproducao.data.mapIndexed
import br.com.duoma.duomaproducao.model.PartExecution
import br.com.duoma.duomaproducao.model.WorkCenter
import br.com.duoma.duomaproducao.ui.Screen
import br.com.duoma.duomaproducao.ui.components.HeatmapChart
import br.com.duoma.duomaproducao.ui.components.SectionHeader
import br.com.duoma.duomaproducao.ui.components.WorkCellDetails
import br.com.duoma.duomaproducao.ui.components.WorkCenterBarChart
import br.com.duoma.duomaproducao.ui.components.WorkCenterLineChart
import br.com.duoma.duomaproducao.viewmodel.monthAverage
import br.com.duoma.duomaproducao.viewmodel.quantityToday
import com.patrykandpatrick.vico.compose.cartesian.data.CartesianChartModelProducer
import com.patrykandpatrick.vico.compose.cartesian.data.columnSeries
import kotlinx.coroutines.launch
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.YearMonth
import kotlinx.datetime.format
import kotlinx.datetime.format.DayOfWeekNames
import kotlinx.datetime.format.char
import kotlinx.datetime.minus
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.yearMonth
import kotlinx.serialization.Serializable
import kotlin.collections.mapValues
import kotlin.time.Clock

@Serializable
data class WorkCenterDetailsScreen(val workCenterName: String) : Screen {
    override val route = "workcenterdetails/$workCenterName"
}
fun LocalDate.Companion.now(): LocalDate =
    Clock.System.now()
        .toLocalDateTime(TimeZone.currentSystemDefault())
        .date
private fun formatProductionCell(cell : String, workCenter : WorkCenter) : String {
    return if (cell.startsWith(workCenter._name, true))
        cell.lowercase().replaceFirstChar { it.uppercaseChar() }
    else "Apontamento automático"
}
@Composable
fun WorkCenterDetailsScreen(workCenter: WorkCenter, executions: List<PartExecution>) {
    val colorScheme = MaterialTheme.colorScheme
    val typography = MaterialTheme.typography

    val mapped = remember (executions, workCenter) {
        executions.map { it.copy(productionCell = formatProductionCell(
            it.productionCell,
            workCenter
        )
        )}
    }

    val sortedDates = remember(executions) {
        executions.map { it.date }.distinct().sorted()
    }
    val quantityByDate = remember(executions) {
        executions.groupBy { it.date }.mapValues { (_, list) -> list.sumOf { it.quantity } }
    }

    val groupedData = remember(executions) {
        executions.groupBy { it.date }
            .entries.sortedByDescending { it.key }
            .map { (date, items) ->
                date to items.groupBy { it.productionCell }.entries.sortedBy { it.key }
            }
    }

    val model = remember { CartesianChartModelProducer() }
    LaunchedEffect(executions) {
        if (sortedDates.isNotEmpty()) {
            model.runTransaction {
                columnSeries { series(sortedDates.map { quantityByDate[it] ?: 0 }) }
            }
        }
    }
    val productionCells = remember(mapped) {
        mapped.fold(mutableSetOf<String>()) {acc, v -> acc += v.productionCell; acc}.toList().sorted()
    }


    val months = remember { (12 downTo 0).map { LocalDate.now() - DatePeriod(months = it) }.toSet() }
    val manager = br.com.duoma.duomaproducao.viewmodel.rememberLineChartManager(
        mapped,
        { exec ->
            exec.groupBy {
                it.productionCell
            }.mapValues { (k, v) ->
                v.groupBy { it.date }.mapValues {
                    if (it.value.size > 1)
                        it.value.sumOf { it.quantity.toDouble() }
                    else 0.0
                }
            }.entries.sortedBy { it.key }.associate {it.key to it.value}
                .mapIndexed { i, k, v -> br.com.duoma.duomaproducao.viewmodel.LineChartSeries(v, k, WC_COLORS[i]) }
        },
        months.toList(),
        { it.month.fullName() },
        null,
        false
    )
    val heatMapData = remember(mapped) {
        mapped.groupBy { it.productionCell }.mapValues {
            it.value.groupBy { it.date.yearMonth }.mapValues { it.value.sumOf { it.quantity.toDouble() } }
        }.flatMap {(cell, list) ->
             list.map {
                 br.com.duoma.duomaproducao.ui.components.HeatmapCell(
                     cell,
                     it.key,
                     it.value.toFloat()
                 )
             }
        }
    }

    val shortDateFmt = remember { LocalDate.Format {
        day()
        char('/')
        monthNumber()
        char('\n')
        dayOfWeek(DayOfWeekNames(
            "SEG", "TER", "QUA", "QUI", "SEX", "SAB", "DOM"
        ))
    } }
    val fullDateFmt = remember { LocalDate.Format {
        dayOfWeek(DayOfWeekNames(
            "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado", "Domingo"
        ))
        chars(", ")
        day()
        char('/')
        monthNumber()
        char('/')
        year()
    } }
    val state = rememberLazyListState()
    val scope = rememberCoroutineScope()
    val isCompact = br.com.duoma.duomaproducao.ui.misc.isCompactScreen()
    val showFab by remember { derivedStateOf { state.firstVisibleItemIndex != 0 } }
    val dateItemIndexes = remember(groupedData) {
        var itemIndex = 1
        groupedData.associate { (date, cellGroups) ->
            val dateIndex = itemIndex
            itemIndex += 1
            cellGroups.forEach { (_, items) -> itemIndex += 1 + items.size }
            date to dateIndex
        }
    }
    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.TopCenter) {
        if (showFab)
            FloatingActionButton(
                {
                    scope.launch {
                        state.animateScrollToItem(0)
                    }
                },
                shape = CircleShape,
                containerColor = colorScheme.primary,
                modifier = Modifier.align(Alignment.BottomEnd).padding(bottom = 40.dp, end = 40.dp)
            ) {
                Icon(
                    Icons.AutoMirrored.Filled.ArrowBack,
                    "Voltar ao topo",
                    tint = Color.White,
                    modifier = Modifier.rotate(90f)
                )
            }
        LazyColumn(
            modifier = Modifier.then(if (isCompact) Modifier.fillMaxWidth() else Modifier.width(1350.dp)).fillMaxSize(),
            contentPadding = PaddingValues(vertical = 28.dp),
            state = state
        ) {
            item {
                Column(Modifier.padding(horizontal = if (isCompact) 16.dp else 32.dp)) {
                    SectionHeader("Produção diária — $workCenter")
                    Spacer(Modifier.height(16.dp))
                    WorkCenterBarChart(
                        model = model,
                        xLabels = sortedDates.map { it.format(shortDateFmt) },
                        xLabelsLinecount = 2,
                        scrollable = true,
                        modifier = Modifier.fillMaxWidth().height(300.dp),
                        onColumnClick = { columnIndex ->
                            val targetIndex = sortedDates
                                .getOrNull(columnIndex)
                                ?.let { dateItemIndexes[it] }
                            if (targetIndex != null) {
                                scope.launch {
                                    state.animateScrollToItem(targetIndex)
                                }
                            }
                        },
                    )
                    Spacer(Modifier.height(40.dp))
                    SectionHeader("Produção por célula de produção")
                    Spacer(Modifier.height(16.dp))
                    WorkCenterLineChart(
                        manager,
                        Modifier.fillMaxWidth()
                    )
                    Spacer(Modifier.height(40.dp))
                    SectionHeader("Produção por célula de produção")
                    Spacer(Modifier.height(16.dp))
                    val heatmapColLabels = remember(months) { months.map { it.month.fullName() to it.yearMonth } }
                    val heatmapRowLabels = remember(productionCells) { productionCells.map { it to it } }
                    HeatmapChart(
                        heatMapData, Modifier.fillMaxWidth(), colLabels = heatmapColLabels,
                        rowLabels = heatmapRowLabels,
                        yLabelWidth = 95.dp
                    )
                    Spacer(Modifier.height(40.dp))
                    SectionHeader("Produção média por célula")
                    Spacer(Modifier.height(40.dp))
                    FlowRow(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(18.dp), horizontalArrangement = Arrangement.SpaceEvenly) {
                        val executionsPerCell = remember(mapped) {mapped.groupBy { it.productionCell } }
                        for (cell in productionCells) {
                            WorkCellDetails(
                                cell.uppercase(),
                                executionsPerCell[cell].quantityToday(),
                                modifier = Modifier.padding(horizontal = if (isCompact) 4.dp else 9.dp)
                                    .size(150.dp, 130.dp),
                                weekAvg = executionsPerCell[cell].monthAverage()
                            )
//                            Spacer(Modifier.weight(1f))
                        }
                    }
                    Spacer(Modifier.height(16.dp))
                    SectionHeader("Itens executados")
                    Spacer(Modifier.height(16.dp))
                    ExecutionTableHeader()
                }
            }

            groupedData.forEach { (date, cellGroups) ->
                stickyHeader(key = date.toString()) {
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .background(colorScheme.background)
                            .padding(horizontal = if (isCompact) 16.dp else 32.dp)
                            .padding(top = if (isCompact) 16.dp else 32.dp, bottom = 8.dp),
                        verticalAlignment = Alignment.Bottom,
                    ) {
                        Text(
                            text = date.format(fullDateFmt).uppercase(),
                            style = typography.titleSmall,
                            fontWeight = FontWeight.ExtraBold,
                            color = colorScheme.primary,
                        )
                        Spacer(Modifier.width(12.dp))
                        HorizontalDivider(
                            Modifier.weight(1f).padding(bottom = 8.dp),
                            color = colorScheme.primary.copy(alpha = 0.2f)
                        )
                        Spacer(Modifier.width(12.dp))
                        val dayTotal = cellGroups.sumOf { it.value.sumOf { item -> item.quantity } }
                        Text(
                            text = "TOTAL DIA: $dayTotal pçs",
                            style = typography.labelSmall,
                            fontWeight = FontWeight.Bold,
                            color = colorScheme.onSurfaceVariant,
                        )
                    }
                }

                cellGroups.forEach { (cell, items) ->
                    item(key = "${date}_$cell") {
                        CellSubHeader(
                            cell,
                            items.sumOf { it.quantity })
                    }
                    items(items, key = { it.identifier() }) { exec ->
                        ExecutionRow(exec)
                        HorizontalDivider(
                            Modifier.padding(start = if (isCompact) 32.dp else 64.dp, end = if (isCompact) 16.dp else 32.dp),
                            color = colorScheme.outlineVariant.copy(alpha = 0.2f)
                        )
                    }
                }
            }

            item { Spacer(Modifier.height(80.dp)) }
        }
        VerticalScrollbar(
            adapter = rememberScrollbarAdapter(state),
            modifier = Modifier
                .align(Alignment.CenterEnd)
                .fillMaxHeight()
                .padding(end = 4.dp)
        )
    }
}

@Composable
private fun CellSubHeader(name: String, quantity: Int) {
    val colorScheme = MaterialTheme.colorScheme
    val isCompact = br.com.duoma.duomaproducao.ui.misc.isCompactScreen()
    Row(
        Modifier
            .fillMaxWidth()
            .padding(horizontal = if (isCompact) 16.dp else 32.dp)
            .padding(top = 16.dp, bottom = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            Modifier
                .width(4.dp)
                .height(16.dp)
                .background(colorScheme.secondary, RoundedCornerShape(2.dp))
        )
        Spacer(Modifier.width(12.dp))
        Text(
            text = name,
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.Bold,
            color = colorScheme.secondary
        )
        Spacer(Modifier.width(16.dp))
        Text(
            text = "$quantity pçs",
            style = MaterialTheme.typography.bodySmall,
            fontWeight = FontWeight.Medium,
            color = colorScheme.onSurfaceVariant
        )
    }
}

@Composable
private fun ExecutionTableHeader() {
    val colorScheme = MaterialTheme.colorScheme
    Row(
        Modifier
            .fillMaxWidth()
            .background(
                colorScheme.surfaceVariant.copy(alpha = 0.5f),
                RoundedCornerShape(topStart = 8.dp, topEnd = 8.dp)
            )
            .padding(horizontal = 12.dp, vertical = 8.dp),
    ) {
        Text(
            "Produto", Modifier.weight(4f),
            style = MaterialTheme.typography.labelSmall,
            fontWeight = FontWeight.Bold,
            color = colorScheme.onSurfaceVariant,
        )
        Text(
            "Pedido", Modifier.weight(1f),
            style = MaterialTheme.typography.labelSmall,
            fontWeight = FontWeight.Bold,
            color = colorScheme.onSurfaceVariant,
        )
        Text(
            "Lote", Modifier.weight(1f),
            style = MaterialTheme.typography.labelSmall,
            fontWeight = FontWeight.Bold,
            color = colorScheme.onSurfaceVariant,
        )
        Text(
            "Qtd", Modifier.weight(1f),
            style = MaterialTheme.typography.labelSmall,
            fontWeight = FontWeight.Bold,
            color = colorScheme.onSurfaceVariant,
            textAlign = TextAlign.End,
        )
    }
}

@Composable
private fun ExecutionRow(exec: PartExecution) {
    val isCompact = br.com.duoma.duomaproducao.ui.misc.isCompactScreen()
    Row(
        Modifier
            .fillMaxWidth()
            .padding(horizontal = if (isCompact) 16.dp else 32.dp)
            .padding(start = if (isCompact) 12.dp else 24.dp, end = 12.dp, top = 6.dp, bottom = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            exec.name, Modifier.weight(4f),
            style = MaterialTheme.typography.bodySmall,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
        Text(exec.order.toString(), Modifier.weight(1f), style = MaterialTheme.typography.bodySmall)
        Text(exec.lot.toString(), Modifier.weight(1f), style = MaterialTheme.typography.bodySmall)
        Text(
            exec.quantity.toString(), Modifier.weight(1f),
            style = MaterialTheme.typography.bodySmall,
            textAlign = TextAlign.End,
            fontWeight = FontWeight.Medium
        )
    }
}
